import { Button } from "@/components/ui/button";
import { AutoGrowTextarea } from "@/components/ui/auto-grow-textarea";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";

export function StringListEditor({
  label,
  items,
  onChange,
  placeholder,
  addLabel = "Add item",
  emptyHint,
}: {
  label: string;
  items: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  addLabel?: string;
  emptyHint?: string;
}) {
  function update(i: number, v: string) {
    onChange(items.map((it, idx) => (idx === i ? v : it)));
  }
  function remove(i: number) {
    onChange(items.filter((_, idx) => idx !== i));
  }
  function move(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  }
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        <span className="text-[10px] text-muted-foreground">{items.length}</span>
      </div>
      {items.length === 0 && emptyHint && (
        <p className="text-xs italic text-muted-foreground">{emptyHint}</p>
      )}
      {items.map((it, i) => (
        <div key={i} className="flex items-start gap-1">
          <AutoGrowTextarea
            value={it}
            onChange={(e) => update(i, e.target.value)}
            placeholder={placeholder}
            className="flex-1 min-h-8 text-sm"
          />
          <Button
            type="button" variant="ghost" size="sm"
            className="h-7 w-7 p-0 text-muted-foreground"
            disabled={i === 0}
            onClick={() => move(i, -1)}
            aria-label="Move up"
          >
            <ArrowUp className="h-3.5 w-3.5" />
          </Button>
          <Button
            type="button" variant="ghost" size="sm"
            className="h-7 w-7 p-0 text-muted-foreground"
            disabled={i === items.length - 1}
            onClick={() => move(i, 1)}
            aria-label="Move down"
          >
            <ArrowDown className="h-3.5 w-3.5" />
          </Button>
          <Button
            type="button" variant="ghost" size="sm"
            className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
            onClick={() => remove(i)}
            aria-label="Remove"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
      <Button
        type="button" variant="ghost" size="sm"
        onClick={() => onChange([...items, ""])}
        className="h-7 px-2 text-xs"
      >
        <Plus className="h-3 w-3 mr-1" />
        {addLabel}
      </Button>
    </div>
  );
}